import React, { useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { Form, Input, Checkbox, Row, Col, message } from 'antd'
import { FormSignIn, CustomButton, GoogleAuthButton, TurnstileCaptcha, isCaptchaEnabled } from 'components'
import { editProfile, logout, signUp } from 'modules/session/session-actions'
import circleUser from 'images/circleUser.png'
import './index.styl'

const { Item } = Form

const checkboxValidate = (_, value) =>
  value
    ? Promise.resolve()
    : Promise.reject(new Error('Please agree to Terms & Conditions'))

const CompleteAccount = ({ onFinishLogin }) => {
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()

  const onFinish = form => {
    setLoading(true)
    dispatch(editProfile({ mobile: form.mobile.trim(), countryCode: '+1' }))
      .then(() => {
        setLoading(false)
        onFinishLogin()
      })
      .catch(() => setLoading(false))
  }

  return (
    <div className='wrapperCompleteAccountInline'>
      <div className='title'>Complete Your Account</div>
      <Form layout='vertical' onFinish={onFinish}>
        <Item
          name='mobile'
          label='Mobile Number'
          rules={[
            { required: true, whitespace: true, message: 'Please enter mobile number' },
            { min: 5, message: 'Mobile number must be at least 5 characters.' },
          ]}
          validateTrigger='onBlur'
        >
          <Input prefix='+1' className='input' type='number' />
        </Item>
        <div className='checkbox'>
          <Item
            name='agreement'
            valuePropName='checked'
            rules={[{ validator: checkboxValidate }]}
          >
            <Checkbox>
              I Accept{' '}
              <Link className='link' to='/help/terms-and-conditions'>
                Terms & Condition
              </Link>
            </Checkbox>
          </Item>
        </div>
        <div className='actions'>
          <CustomButton text='Go back' type='white' htmlType='button' className='goBackBtn' onClick={() => dispatch(logout())} />
          <CustomButton text='Continue' type='dark' htmlType='submit' className='continueBtn' loading={loading} />
        </div>
      </Form>
    </div>
  )
}

const InlineSignUp = ({ onBack }) => {
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const [captchaToken, setCaptchaToken] = useState(null)
  const captchaRef = useRef(null)
  const dispatch = useDispatch()

  const onFinish = values => {
    if (isCaptchaEnabled && !captchaToken) {
      message.error('Please complete the captcha')
      return
    }
    setLoading(true)
    const obj = { ...values, deviceType: 'WEB', countryCode: '+1' }
    if (isCaptchaEnabled) obj.captchaToken = captchaToken
    Object.keys(values).forEach(i => {
      if (i === 'agreement') {
        delete obj[i]
      } else {
        obj[i] = values[i].trim()
      }
    })
    dispatch(signUp(obj, onBack)).then(() => {
      setLoading(false)
      if (captchaRef.current) captchaRef.current.reset()
    })
  }

  const onPressSpace = e => {
    if (e.key === ' ') {
      e.preventDefault()
    }
  }

  const onComparePasswords = async (rule, value) => {
    if (rule.field === 'password_confirm') {
      if (value && value !== form.getFieldValue('password')) {
        throw new Error('Passwords do not match.')
      }
    } else if (rule.field === 'password') {
      const confirmValue = form.getFieldValue('password_confirm')
      if (value && confirmValue && value !== form.getFieldValue('password_confirm')) {
        throw new Error('Passwords do not match.')
      }
    }
  }

  const getRule = (label, name) => {
    const rules = [{ required: true, whitespace: true, message: 'Please enter ' + label.toLowerCase() }]
    if (name === 'email') {
      return rules.concat({ type: 'email', transform: value => value?.trim() })
    }
    if (name === 'password') {
      return rules.concat(
        { min: 6, message: 'Password length must be at least 6 characters' },
        { validator: onComparePasswords },
      )
    }
    if (name === 'password_confirm') {
      return rules.concat({ validator: onComparePasswords })
    }
    if (name === 'mobile') {
      return rules.concat({ min: 5, message: `${name} must be at least 5 characters.` })
    }
    return rules
  }

  const CustomItem = (name, label, type) =>
    name === 'password_confirm' ? (
      <Item name={name} label={label} dependencies={['password']} rules={getRule(label, name)} validateTrigger='onBlur'>
        <Input.Password onKeyDown={onPressSpace} className='input' />
      </Item>
    ) : (
      <Item name={name} label={label} rules={getRule(label, name)} validateTrigger='onBlur'>
        {type === 'password' ? (
          <Input.Password onKeyDown={onPressSpace} className='input' />
        ) : name === 'mobile'
          ? <Input prefix='+1' className='input' type='number' />
          : <Input className='input' />}
      </Item>
    )

  return (
    <div className='wrapperSignUpInline'>
      <div className='title'>Create your account using your email</div>
      <Form form={form} layout='vertical' onFinish={onFinish}>
        <Input.Group>
          <Row gutter={16}>
            <Col span={12}>{CustomItem('firstName', 'First Name')}</Col>
            <Col span={12}>{CustomItem('lastName', 'Last Name')}</Col>
          </Row>
        </Input.Group>
        {CustomItem('email', 'Email')}
        {CustomItem('mobile', 'Mobile Number')}
        {CustomItem('password', 'Create Password', 'password')}
        {CustomItem('password_confirm', 'Confirm Password', 'password')}
        <div className='checkbox'>
          <Item name='agreement' valuePropName='checked' rules={[{ validator: checkboxValidate }]}>
            <Checkbox>
              I Accept{' '}
              <Link className='link' to='/help/terms-and-conditions'>
                Terms & Condition
              </Link>
            </Checkbox>
          </Item>
        </div>
        <TurnstileCaptcha ref={captchaRef} onToken={setCaptchaToken} />
        <div className='actions'>
          <CustomButton text='Go back' type='white' htmlType='button' className='goBackBtn' onClick={onBack} />
          <CustomButton text='Save & Continue' type='dark' htmlType='submit' className={`saveContinueBtn${loading ? ' loading' : ''}`} loading={loading} />
        </div>
        <GoogleAuthButton onAuthSuccess={() => {}} />
        <div className='linkToLogInWrapper'>
          <div className='noAccountTitle'>Already have an account?</div>
          <div className='logInButtonLink' onClick={onBack}>Log In</div>
        </div>
      </Form>
    </div>
  )
}

const Login = ({ onChangeScreen, setAuthorizedBuy }) => {
  const { user } = useSelector(state => state.session)
  const [showSignUp, setShowSignUp] = useState(false)
  const onFinishLogin = () => {
    onChangeScreen('payment', true)
    setAuthorizedBuy(true)
  }
  const goGuest = () => {
    setAuthorizedBuy(false).then(() => onChangeScreen('guest'))
  }
  return (
    <div className='screen screenLogin'>
      {user
        ? (
          user.mobile
            ? (
              <div className='wrapperUserModal'>
                <img src={circleUser} alt='circleUser' className='icon' />
                <div className='title'>You are logged in as {user.userName}</div>

                <div className='actions'>
                  <CustomButton text='Next' type='dark' onClick={onFinishLogin} />
                </div>
              </div>
            )
            : <CompleteAccount onFinishLogin={onFinishLogin} />
        )
        : (
          showSignUp
            ? <InlineSignUp onBack={() => setShowSignUp(false)} />
            : <FormSignIn title='Please Log In' onFinishLogin={onFinishLogin} goGuest={goGuest} onSignUpClick={() => setShowSignUp(true)} />
        )}
    </div>
  )
}

export default Login
